import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Mic, Users, Tv, Smartphone, QrCode, Star, CheckCircle, ArrowRight, Music, Play, Clock, User, ChevronLeft, ChevronRight, Radio } from 'lucide-react';

const KaraokeLandingPage = () => {
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [mobileRequestIndex, setMobileRequestIndex] = useState(0);
  const [queueItems, setQueueItems] = useState([
    { id: 1, song: "Sweet Caroline", artist: "Neil Diamond", singer: "Sarah", time: "2:30" },
    { id: 2, song: "Don't Stop Believin'", artist: "Journey", singer: "Mike", time: "4:15" },
    { id: 3, song: "Bohemian Rhapsody", artist: "Queen", singer: "Emma", time: "5:55" },
  ]);
  const [currentSong, setCurrentSong] = useState("Sweet Caroline");
  const [currentSinger, setCurrentSinger] = useState("Sarah");

  // Auto-rotate carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % 3);
    }, 5000); // Change device every 5 seconds
    return () => clearInterval(interval);
  }, []);

  // Animate mobile request form (only when mobile is shown)
  useEffect(() => {
    if (carouselIndex !== 0) return;
    const interval = setInterval(() => {
      setMobileRequestIndex((prev) => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(interval);
  }, [carouselIndex]);

  // Animate queue updates (only when laptop or TV is shown)
  useEffect(() => {
    if (carouselIndex === 0) return;
    const interval = setInterval(() => {
      setQueueItems((prev) => {
        const newQueue = [...prev];
        // Move first item to end (simulating completion)
        const completed = newQueue.shift();
        if (completed) {
          newQueue.push({
            ...completed,
            id: Date.now(),
            song: ["I Will Survive", "Livin' on a Prayer", "Dancing Queen", "We Are The Champions"][Math.floor(Math.random() * 4)],
            singer: ["Alex", "Jordan", "Taylor", "Casey"][Math.floor(Math.random() * 4)],
          });
          setCurrentSong(completed.song);
          setCurrentSinger(completed.singer);
        }
        return newQueue;
      });
    }, 4000);
    return () => clearInterval(interval);
  }, [carouselIndex]);

  const mobileRequestSteps = [
    { song: "Sweet Caroline", artist: "Neil Diamond", singer: "Sarah" },
    { song: "Don't Stop Believin'", artist: "Journey", singer: "Mike" },
    { song: "Bohemian Rhapsody", artist: "Queen", singer: "Emma" },
  ];

  return (
    <>
      <Helmet>
        <title>Karaoke Mode - Your Song Request</title>
        <meta name="description" content="Perfect for DJs and Karaoke Hosts. Manage song requests, moderate content, and display live queues on venue screens." />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-purple-950 via-purple-900 to-indigo-950 text-white">
        {/* Header */}
        <header className="border-b border-purple-800/50 bg-purple-900/20 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-4">
              <div className="flex items-center">
                <Link to="/" className="inline-flex items-center text-purple-300 hover:text-purple-200 mr-4">
                  ← Back to Main Site
                </Link>
                <span className="text-2xl font-bold text-purple-200">🎤 Karaoke Mode</span>
              </div>
              <div className="flex items-center space-x-3">
                <Link to="/karaoke/signup">
                  <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                    Sign Up
                  </Button>
                </Link>
                <Link to="/karaoke/login">
                  <Button variant="outline" className="border-purple-600 text-purple-300 hover:bg-purple-600 hover:text-white">
                    Sign In
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Hero Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-7xl font-bold text-purple-100 mb-6">
              🎤 Karaoke Mode
            </h1>
            <p className="text-xl text-purple-300 max-w-4xl mx-auto mb-8">
              Perfect for DJs and Karaoke Hosts. Let your audience request any song, 
              moderate requests, and display live queues on venue screens.
            </p>
            {/* Three-Device Visual Preview - Carousel */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mb-12 relative"
            >
              {/* Background glow effect */}
              <div className="absolute inset-0 -z-10 bg-gradient-to-r from-purple-600/20 via-pink-600/20 to-indigo-600/20 blur-3xl rounded-3xl" />
              
              {/* Carousel Container */}
              <div className="relative max-w-[1400px] mx-auto">
                {/* Carousel Items */}
                <div className="relative h-[700px] overflow-hidden">
                  <AnimatePresence mode="popLayout">
                    {/* Stage 1: Mobile - Make a Request */}
                    {carouselIndex === 0 && (
                      <motion.div
                        key="mobile"
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="absolute inset-0 flex items-center justify-center"
                      >
                        <div className="relative">
                          {/* Glow effect */}
                          <motion.div
                            animate={{
                              boxShadow: [
                                "0 0 30px rgba(168, 85, 247, 0.3)",
                                "0 0 50px rgba(168, 85, 247, 0.5)",
                                "0 0 30px rgba(168, 85, 247, 0.3)",
                              ],
                            }}
                            transition={{ duration: 2, repeat: Infinity }}
                            className="absolute inset-0 rounded-[40px]"
                          />
                          <div className="relative w-[320px] rounded-[40px] border-2 border-purple-500/50 bg-gradient-to-b from-purple-900/60 via-purple-950/80 to-black shadow-2xl overflow-hidden backdrop-blur-sm" style={{ height: '640px' }}>
                            {/* Notch */}
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-black/80 rounded-b-3xl z-10" />
                            
                            {/* Status bar */}
                            <div className="pt-8 px-4 pb-2 flex justify-between items-center text-[10px] text-purple-200/60">
                              <span>9:41</span>
                              <div className="flex gap-1">
                                <div className="w-4 h-2 border border-purple-300/40 rounded-sm">
                                  <div className="w-full h-full bg-purple-300/60 rounded-sm" />
                                </div>
                              </div>
                            </div>

                            <div className="p-5 pt-3 space-y-4 h-full overflow-y-auto">
                              {/* Header */}
                              <div className="text-center">
                                <motion.div
                                  initial={{ scale: 0.9 }}
                                  animate={{ scale: 1 }}
                                  transition={{ duration: 0.3, repeat: Infinity, repeatType: "reverse", repeatDelay: 2 }}
                                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-purple-600/40 to-pink-600/40 border border-purple-400/50 text-purple-100 shadow-lg"
                                >
                                  <Music className="w-3.5 h-3.5" />
                                  Request Your Song
                                </motion.div>
                              </div>

                              {/* Request Form */}
                              <AnimatePresence mode="wait">
                                <motion.div
                                  key={mobileRequestIndex}
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -10 }}
                                  transition={{ duration: 0.5 }}
                                  className="bg-gradient-to-br from-purple-800/40 to-purple-900/60 border border-purple-600/50 rounded-2xl p-4 space-y-3 shadow-xl"
                                  style={{ minHeight: '300px' }}
                                >
                                  <div className="flex items-center gap-2 mb-2">
                                    <Smartphone className="w-4 h-4 text-purple-300" />
                                    <p className="text-xs font-semibold text-purple-200">Make a Request</p>
                                  </div>
                                  
                                  {/* Song Input */}
                                  <div className="space-y-1.5">
                                    <label className="text-[10px] text-purple-300/80">Song Title</label>
                                    <div className="h-10 rounded-lg bg-purple-900/60 border border-purple-700/50 flex items-center px-3">
                                      <span className="text-xs text-purple-100 font-medium truncate block w-full">
                                        {mobileRequestSteps[mobileRequestIndex].song}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Artist Input */}
                                  <div className="space-y-1.5">
                                    <label className="text-[10px] text-purple-300/80">Artist</label>
                                    <div className="h-10 rounded-lg bg-purple-900/60 border border-purple-700/50 flex items-center px-3">
                                      <span className="text-xs text-purple-100 font-medium truncate block w-full">
                                        {mobileRequestSteps[mobileRequestIndex].artist}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Singer Name */}
                                  <div className="space-y-1.5">
                                    <label className="text-[10px] text-purple-300/80">Your Name</label>
                                    <div className="h-10 rounded-lg bg-purple-900/60 border border-purple-700/50 flex items-center px-3">
                                      <span className="text-xs text-purple-100 font-medium truncate block w-full">
                                        {mobileRequestSteps[mobileRequestIndex].singer}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Submit Button */}
                                  <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="w-full h-10 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold shadow-lg shadow-purple-600/30 flex items-center justify-center gap-2 mt-2"
                                  >
                                    <Play className="w-3.5 h-3.5" />
                                    Request a Song
                                  </motion.button>
                                </motion.div>
                              </AnimatePresence>

                              {/* Queue Preview */}
                              <div className="bg-purple-900/30 border border-purple-700/40 rounded-xl p-3">
                                <p className="text-[10px] text-purple-300/70 mb-2">Request Queue ({queueItems.length})</p>
                                <div className="space-y-1.5">
                                  {queueItems.slice(0, 2).map((item, i) => (
                                    <div key={i} className="flex items-center gap-2 p-2 rounded bg-purple-800/30">
                                      <div className="w-5 h-5 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                                        <span className="text-white text-[9px] font-bold">{i + 1}</span>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-[10px] text-green-300 font-semibold truncate">{item.song}</p>
                                        <p className="text-[9px] text-purple-400/70 truncate">by {item.artist}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Stage 2: Laptop - Manage Requests */}
                    {carouselIndex === 1 && (
                      <motion.div
                        key="laptop"
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="absolute inset-0 flex items-center justify-center"
                      >
                        <div className="relative w-full max-w-[900px]" style={{ aspectRatio: '16/9' }}>
                          {/* Glow effect */}
                          <motion.div
                            animate={{
                              boxShadow: [
                                "0 0 40px rgba(139, 92, 246, 0.3)",
                                "0 0 60px rgba(139, 92, 246, 0.5)",
                                "0 0 40px rgba(139, 92, 246, 0.3)",
                              ],
                            }}
                            transition={{ duration: 2.5, repeat: Infinity }}
                            className="absolute inset-0 rounded-2xl"
                          />
                          
                          <div className="relative h-full rounded-2xl border-2 border-purple-500/50 bg-gradient-to-b from-gray-900/95 to-black shadow-2xl overflow-hidden backdrop-blur-sm flex flex-col">
                            {/* Top bar */}
                            <div className="h-8 bg-black/60 border-b border-purple-800/40 flex items-center px-4 gap-2 flex-shrink-0">
                              <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                              <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                              <span className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
                              <span className="ml-4 text-[10px] text-purple-200/70 font-medium">Karaoke Live Dashboard</span>
                            </div>
                            
                            <div className="p-5 bg-gradient-to-br from-purple-950/50 to-gray-900/80 flex-1 overflow-hidden flex flex-col">
                              {/* Header with Live Status */}
                              <div className="flex items-center justify-between mb-4 flex-shrink-0">
                                <motion.div
                                  animate={{ scale: [1, 1.05, 1] }}
                                  transition={{ duration: 2, repeat: Infinity }}
                                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-bold bg-gradient-to-r from-red-600/60 to-red-500/60 border border-red-400/50 text-white shadow-lg shadow-red-500/30"
                                >
                                  <motion.div
                                    animate={{ opacity: [1, 0.5, 1] }}
                                    transition={{ duration: 1, repeat: Infinity }}
                                    className="w-2.5 h-2.5 rounded-full bg-white"
                                  />
                                  LIVE
                                </motion.div>
                                <div className="text-sm text-purple-200/70 font-medium">
                                  <Users className="w-4 h-4 inline mr-1" />
                                  {queueItems.length} in Queue
                                </div>
                              </div>

                              {/* Stats Row */}
                              <div className="grid grid-cols-3 gap-3 mb-4 flex-shrink-0">
                                <div className="bg-purple-800/30 border border-purple-700/50 rounded-lg p-2.5 text-center">
                                  <div className="text-lg font-bold text-purple-100">{queueItems.length}</div>
                                  <div className="text-[10px] text-purple-300/80">Total Requests</div>
                                </div>
                                <div className="bg-purple-800/30 border border-purple-700/50 rounded-lg p-2.5 text-center">
                                  <div className="text-lg font-bold text-green-400">3</div>
                                  <div className="text-[10px] text-purple-300/80">Approved</div>
                                </div>
                                <div className="bg-purple-800/30 border border-purple-700/50 rounded-lg p-2.5 text-center">
                                  <div className="text-lg font-bold text-yellow-400">1</div>
                                  <div className="text-[10px] text-purple-300/80">Pending</div>
                                </div>
                              </div>

                              {/* Queue Section */}
                              <div className="bg-purple-900/40 border border-purple-700/50 rounded-xl p-4 mb-3 shadow-xl flex-1 overflow-hidden flex flex-col">
                                <div className="flex items-center justify-between mb-3 flex-shrink-0">
                                  <div className="flex items-center gap-2">
                                    <Music className="w-5 h-5 text-purple-300" />
                                    <h3 className="text-base font-bold text-purple-100">Incoming Request Queue</h3>
                                  </div>
                                  <span className="text-xs text-purple-300/70">Approved songs appear here</span>
                                </div>
                                
                                <div className="flex-1 overflow-y-auto space-y-2.5 min-h-0">
                                  <AnimatePresence>
                                    {queueItems.slice(0, 5).map((item, index) => (
                                      <motion.div
                                        key={item.id}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: 20 }}
                                        transition={{ duration: 0.4, delay: index * 0.1 }}
                                        className="bg-gradient-to-r from-purple-800/50 to-purple-900/50 border border-purple-600/50 rounded-lg p-3 flex items-start gap-3 flex-shrink-0"
                                      >
                                        <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center relative flex-shrink-0">
                                          <span className="text-white text-sm font-bold">{index + 1}</span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 mb-1">
                                            <p className="text-sm text-purple-100 font-semibold truncate">{item.song}</p>
                                          </div>
                                          <p className="text-xs text-purple-300/80 truncate mb-1">{item.artist}</p>
                                          <div className="flex items-center gap-2 text-[11px] text-purple-400/70">
                                            <User className="w-3 h-3" />
                                            <span className="truncate">{item.singer}</span>
                                            <span className="mx-1">•</span>
                                            <Clock className="w-3 h-3" />
                                            <span>{item.time}</span>
                                          </div>
                                        </div>
                                        <motion.button
                                          whileHover={{ scale: 1.05 }}
                                          whileTap={{ scale: 0.95 }}
                                          className="h-8 px-3 rounded bg-green-600/80 hover:bg-green-600 text-white text-xs font-medium flex-shrink-0"
                                        >
                                          ✓ Done
                                        </motion.button>
                                      </motion.div>
                                    ))}
                                  </AnimatePresence>
                                </div>
                              </div>

                              {/* Pending Requests Section */}
                              <div className="bg-purple-900/30 border border-purple-700/40 rounded-lg p-3 flex-shrink-0">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs font-semibold text-purple-200">Pending Approval</span>
                                  <span className="text-xs text-yellow-400">1 request</span>
                                </div>
                                <div className="bg-yellow-900/20 border border-yellow-600/30 rounded p-2.5 flex items-center gap-2">
                                  <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs text-yellow-300 font-medium truncate">I Will Survive</p>
                                    <p className="text-[10px] text-yellow-400/70 truncate">by Gloria Gaynor</p>
                                  </div>
                                  <div className="flex gap-1.5">
                                    <motion.button
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      className="h-6 px-2 rounded bg-green-600/80 text-white text-[10px] font-medium"
                                    >
                                      Approve
                                    </motion.button>
                                    <motion.button
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      className="h-6 px-2 rounded bg-red-600/80 text-white text-[10px] font-medium"
                                    >
                                      Reject
                                    </motion.button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Stage 3: TV - Venue Display with QR Code */}
                    {carouselIndex === 2 && (
                      <motion.div
                        key="tv"
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="absolute inset-0 flex items-center justify-center"
                      >
                        <div className="relative w-full max-w-[1200px] h-full">
                          {/* Glow effect */}
                          <motion.div
                            animate={{
                              boxShadow: [
                                "0 0 50px rgba(124, 58, 237, 0.3)",
                                "0 0 70px rgba(124, 58, 237, 0.5)",
                                "0 0 50px rgba(124, 58, 237, 0.3)",
                              ],
                            }}
                            transition={{ duration: 3, repeat: Infinity }}
                            className="absolute inset-0 rounded-2xl"
                          />
                          
                          <div className="relative h-full rounded-2xl border-4 border-purple-900/80 bg-gradient-to-br from-black via-purple-950/90 to-black shadow-2xl overflow-hidden flex flex-col p-6">
                            {/* Main Content Grid - Matching actual TV Display */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 overflow-hidden">
                              {/* Main Content - 2 columns */}
                              <div className="lg:col-span-2 flex flex-col gap-6 overflow-hidden">
                                {/* Live Session Info Card */}
                                <motion.div
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ duration: 0.4 }}
                                  className="bg-purple-900/30 border border-purple-700/50 rounded-xl p-5 backdrop-blur-sm flex-shrink-0"
                                >
                                  <div className="flex items-center mb-4">
                                    <motion.div
                                      animate={{ rotate: [0, 10, -10, 0] }}
                                      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                                    >
                                      <Radio className="w-6 h-6 text-red-400 mr-3" />
                                    </motion.div>
                                    <h2 className="text-2xl font-bold text-purple-100">Live Karaoke Session</h2>
                                  </div>
                                  <p className="text-purple-300 text-lg mb-4">Join the fun! Scan the QR code to request songs</p>
                                  
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-purple-800/30 rounded-lg p-4 text-center">
                                      <Users className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                                      <div className="text-2xl font-bold text-purple-100">{queueItems.length}</div>
                                      <div className="text-sm text-purple-300">Songs Requested</div>
                                    </div>
                                    <div className="bg-purple-800/30 rounded-lg p-4 text-center">
                                      <Clock className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                                      <div className="text-2xl font-bold text-purple-100">
                                        {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </div>
                                      <div className="text-sm text-purple-300">Current Time</div>
                                    </div>
                                  </div>
                                </motion.div>

                                {/* Live Request Queue Card */}
                                <motion.div
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ duration: 0.4, delay: 0.1 }}
                                  className="bg-purple-900/30 border border-purple-700/50 rounded-xl p-5 backdrop-blur-sm flex-1 overflow-hidden flex flex-col"
                                >
                                  <div className="flex items-center mb-4 flex-shrink-0">
                                    <motion.div
                                      animate={{ rotate: [0, 5, -5, 0] }}
                                      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                    >
                                      <Music className="w-6 h-6 text-purple-300 mr-2" />
                                    </motion.div>
                                    <h3 className="text-xl font-bold text-purple-100">Live Request Queue</h3>
                                  </div>
                                  <p className="text-purple-300 text-base mb-4 flex-shrink-0">Songs requested by the audience during this live session</p>
                                  
                                  <div className="flex-1 overflow-y-auto space-y-4 min-h-0">
                                    <AnimatePresence mode="popLayout">
                                      {queueItems.slice(0, 3).map((item, index) => (
                                        <motion.div
                                          key={item.id}
                                          layout
                                          initial={{ opacity: 0, x: -20 }}
                                          animate={{ opacity: 1, x: 0 }}
                                          exit={{ opacity: 0, scale: 0.8 }}
                                          transition={{ duration: 0.3, delay: index * 0.1 }}
                                          className="bg-purple-800/30 border border-purple-700/50 rounded-lg p-5 flex items-center gap-4 flex-shrink-0"
                                        >
                                          <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-lg font-bold text-white flex-shrink-0">
                                            {index + 1}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="text-lg text-purple-100 font-semibold truncate mb-1">
                                              {item.song}
                                            </div>
                                            <div className="text-base text-purple-300 truncate">
                                              by {item.artist}
                                            </div>
                                          </div>
                                        </motion.div>
                                      ))}
                                    </AnimatePresence>
                                    {queueItems.length > 3 && (
                                      <div className="text-center text-purple-300 text-base pt-2">
                                        +{queueItems.length - 3} more {queueItems.length - 3 === 1 ? 'song' : 'songs'} in the queue
                                      </div>
                                    )}
                                  </div>
                                </motion.div>
                              </div>

                              {/* Sidebar - QR Code - 1 column */}
                              <div className="lg:col-span-1 flex-shrink-0">
                                <motion.div
                                  initial={{ opacity: 0, x: 20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ duration: 0.4, delay: 0.2 }}
                                  className="bg-purple-900/30 border border-purple-700/50 rounded-xl p-5 backdrop-blur-sm h-full flex flex-col"
                                >
                                  <div className="flex items-center mb-4">
                                    <motion.div
                                      animate={{ scale: [1, 1.1, 1] }}
                                      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                                    >
                                      <QrCode className="w-6 h-6 text-purple-300 mr-2" />
                                    </motion.div>
                                    <h3 className="text-xl font-bold text-purple-100">Request Songs</h3>
                                  </div>
                                  <p className="text-purple-300 text-base mb-4">Scan this QR code to request songs</p>
                                  
                                  <div className="flex-1 flex flex-col items-center justify-center">
                                    <motion.div 
                                      className="bg-white p-4 rounded-lg mb-4"
                                      whileHover={{ scale: 1.05 }}
                                      transition={{ duration: 0.3 }}
                                    >
                                      <div className="w-40 h-40 bg-gray-200 rounded flex items-center justify-center">
                                        <QrCode className="w-32 h-32 text-gray-600" />
                                      </div>
                                    </motion.div>
                                    <p className="text-purple-300 text-sm text-center mb-4">
                                      Point your phone camera at this QR code to open the song request page
                                    </p>
                                    
                                    <div className="mt-auto pt-4 border-t border-purple-600/30 w-full">
                                      <div className="text-center">
                                        <div className="text-purple-300 text-sm font-medium">
                                          Brought to you by:
                                        </div>
                                        <div className="text-purple-200 text-base font-bold mt-1">
                                          YourSongRequest.com
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </motion.div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Carousel Navigation */}
                <div className="flex flex-col items-center gap-4 mt-6">
                  <div className="flex items-center justify-center gap-4">
                    {/* Previous Button */}
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setCarouselIndex((prev) => (prev - 1 + 3) % 3)}
                      className="w-10 h-10 rounded-full bg-purple-800/50 border border-purple-600/50 text-purple-200 hover:bg-purple-700/50 flex items-center justify-center transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </motion.button>

                    {/* Dots Indicator */}
                    <div className="flex items-center gap-2">
                      {[0, 1, 2].map((index) => (
                        <motion.button
                          key={index}
                          onClick={() => setCarouselIndex(index)}
                          className={`w-3 h-3 rounded-full transition-all ${
                            carouselIndex === index
                              ? 'bg-purple-400 w-8'
                              : 'bg-purple-600/50 hover:bg-purple-600/70'
                          }`}
                          whileHover={{ scale: 1.2 }}
                          whileTap={{ scale: 0.9 }}
                        />
                      ))}
                    </div>

                    {/* Next Button */}
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setCarouselIndex((prev) => (prev + 1) % 3)}
                      className="w-10 h-10 rounded-full bg-purple-800/50 border border-purple-600/50 text-purple-200 hover:bg-purple-700/50 flex items-center justify-center transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </motion.button>
                  </div>

                  {/* Stage Labels - Below Navigation */}
                  <motion.div
                    key={carouselIndex}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex items-center gap-2 bg-purple-900/30 border border-purple-700/50 rounded-full px-6 py-2 backdrop-blur-sm"
                  >
                    {carouselIndex === 0 && (
                      <>
                        <Smartphone className="w-4 h-4 text-purple-300" />
                        <span className="text-sm font-semibold text-purple-200">Stage 1: Make a Request</span>
                      </>
                    )}
                    {carouselIndex === 1 && (
                      <>
                        <Users className="w-4 h-4 text-purple-300" />
                        <span className="text-sm font-semibold text-purple-200">Stage 2: Manage Requests</span>
                      </>
                    )}
                    {carouselIndex === 2 && (
                      <>
                        <Tv className="w-4 h-4 text-purple-300" />
                        <span className="text-sm font-semibold text-purple-200">Stage 3: Venue Display</span>
                      </>
                    )}
                  </motion.div>
                </div>
              </div>
            </motion.div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/karaoke/signup">
                <Button size="lg" className="bg-purple-600 hover:bg-purple-700 text-white font-bold text-lg py-4 px-8 rounded-lg transform hover:scale-105 transition-transform duration-300">
                  Get Started Free
                </Button>
              </Link>
              <Link to="/karaoke/login">
                <Button size="lg" variant="outline" className="text-purple-200 border-purple-600 hover:bg-purple-600 hover:text-white font-bold text-lg py-4 px-8 rounded-lg transform hover:scale-105 transition-transform duration-300">
                  Sign In
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Hero Stats */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20"
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-purple-300" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Any Song</h3>
              <p className="text-purple-200">No setlist limitations</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Tv className="w-8 h-8 text-purple-300" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Venue Display</h3>
              <p className="text-purple-200">Show queues on screens</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-purple-300" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Skip Queue</h3>
              <p className="text-purple-200">Premium feature for VIP requests</p>
            </div>
          </motion.div>

          {/* Features Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-20"
          >
            <h2 className="text-3xl font-bold text-purple-100 text-center mb-12">Why Choose Karaoke Mode?</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mic className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-purple-100 mb-2">Unlimited Song Requests</h3>
                    <p className="text-purple-300">Your audience can request any song, not just from a limited setlist. Perfect for karaoke where anything goes!</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Tv className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-purple-100 mb-2">Venue Display Integration</h3>
                    <p className="text-purple-300">Show live queues on TV screens and projectors around your venue. Keep everyone engaged and excited!</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Smartphone className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-purple-100 mb-2">Mobile QR Codes</h3>
                    <p className="text-purple-300">Audience scans QR codes to request songs from their phones. No apps to download!</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Star className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-purple-100 mb-2">Skip Queue Feature</h3>
                    <p className="text-purple-300">Premium requests that jump to the front of the queue. Set your own pricing for this VIP service!</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-purple-100 mb-2">Content Moderation</h3>
                    <p className="text-purple-300">Review and approve requests before they appear in the queue. Keep it family-friendly and appropriate!</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <QrCode className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-purple-100 mb-2">Easy Setup</h3>
                    <p className="text-purple-300">Get started in minutes. No complex setup required. Just create your account and start accepting requests!</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-center"
          >
            <div className="bg-purple-900/30 border border-purple-700/50 rounded-lg p-8">
              <h2 className="text-2xl font-bold text-purple-100 mb-4">Ready to Transform Your Karaoke Night?</h2>
              <p className="text-purple-300 mb-6 max-w-2xl mx-auto">
                Join thousands of DJs and karaoke hosts who are already using YSR to create unforgettable experiences for their audiences.
              </p>
              <Link to="/karaoke/signup">
                <Button size="lg" className="bg-purple-600 hover:bg-purple-700 text-white font-bold text-lg py-4 px-8 rounded-lg transform hover:scale-105 transition-transform duration-300">
                  Start Free Today
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </main>
      </div>
    </>
  );
};

export default KaraokeLandingPage;
